import { ChangeEvent, ReactNode, useState } from 'react';
import { FormControlLabel, FormControlLabelProps, styled, Switch } from '@mui/material';

import { COLORS } from '~/styles/colors';
import { logger } from '~/utils/logger';

type AsyncSwitchProps = Omit<FormControlLabelProps, 'control' | 'onChange'> & {
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => Promise<void> | void;
  checked: boolean;
  label?: ReactNode;
};

export default function AsyncSwitch({ disabled, onChange, checked, label, ...props }: AsyncSwitchProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    try {
      await onChange(e);
    } catch (err) {
      logger.error(err);
    }
    setIsLoading(false);
  };

  return (
    <StyledFormControlLabel
      {...props}
      control={
        <StyledSwitch
          disabled={isLoading || disabled}
          checked={checked}
          onChange={handleChange}
          focusVisibleClassName=".Mui-focusVisible"
          disableRipple
        />
      }
      label={label}
    />
  );
}

const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  margin: 0,
  gap: '8px',
  '.MuiFormControlLabel-label': {
    color: COLORS.textMain,
  },
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 36,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff !important',
      '& + .MuiSwitch-track': {
        backgroundColor: COLORS.primary,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#fff',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
    boxShadow: '0 2px 4px 0 rgba(39, 39, 39, 0.1)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: COLORS.grey600,
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));
